import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { map, Observable, startWith } from 'rxjs';
import { getFileDisplayState } from 'src/entities/file';
import { FileService } from 'src/app/services/api/file.service';
import { CognitoService } from 'src/app/services/cognito.service';
import { LoadingService } from 'src/app/services/loading.service';
import { File } from './models';
import { Periodo } from './models/Periodo';

@Component({
  selector: 'app-reporte-file',
  templateUrl: './reporte-file.component.html',
  styleUrls: ['./reporte-file.component.css']
})
export class ReporteFileComponent implements OnInit {
  // === DATOS PRINCIPALES ===
  allFiles: File[] = []; // Todos los archivos sin filtrar (fuente única de verdad)

  // === DATOS DERIVADOS (calculados automáticamente) ===
  fileDataSource = new MatTableDataSource<File>(); // Para la tabla de archivos
  fileCountsDataSource = new MatTableDataSource<any>(); // Para conteos
  allPeriodos: Periodo[] = []; // Períodos extraídos de allFiles
  totalTotal: number = 0; // Total de archivos

  // === CONTROLES Y FILTROS ===
  fileControl = new FormControl<string | any>('');
  filteredOptions!: Observable<any[]>;
  searchValue: string = '';
  selectedPeriodo: string = '';

  // === CONFIGURACIÓN ===
  isFileHijo = false;
  isLoading = false;

  fileDisplayedColumns2 = [
    'TOTAL',
    'NUEVO PEDIDO',
    'LAST MINUTE',
    'REVISAR',
    'POSTERGADO',
    'EN PROCESO',
    'CONFIRMADO',
    'RECONFIRMADO',
    'PROGRAMADO',
    'EN OPERACION',
    'OPERADO',
    'CERRADO'
  ];

  constructor(
    private readonly fileService: FileService,
    private readonly changeDetectorRefs: ChangeDetectorRef,
    public dialog: MatDialog,
    public loadingService: LoadingService,
    public cognitoService: CognitoService
  ) {}

  ngOnInit(): void {
    this.loadMain();
    this.initializeAutocomplete();
  }

  private initializeAutocomplete(): void {
    this.filteredOptions = this.fileControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const codigo = typeof value === 'string' ? value : value?.codigo;
        return codigo
          ? this._filterPeriodos(codigo as string)
          : this.allPeriodos.slice();
      })
    );
  }

  // === CARGA INICIAL DE DATOS ===
  loadMain(): void {
    this.isLoading = true;

    this.fileService.getAllFiles().subscribe({
      next: (dataFiles) => {
        // Procesar archivos según configuración
        let processedFiles = this.isFileHijo
          ? dataFiles.getAllFiles.filter(
              (item: any) => item.filePadreId !== null
            )
          : dataFiles.getAllFiles;

        // Filtrar archivos válidos
        processedFiles = processedFiles.filter(
          (item: any) =>
            item.estadoFile !== null &&
            item.estadoFile !== 'APROBADO' &&
            item.estadoFile !== 'ANULADO'
        );

        // Guardar en la fuente única de verdad
        this.allFiles = processedFiles.map((file: any) => {
          return {
            ...file,
            fileDisplayState: getFileDisplayState({
              estadoFile: file.estadoFile,
              lastMinute: file.lastMinute,
              pendingPreOpIncidents: file.pendingPreOpIncidents,
              estadoItinerario: file.estadoItinerario
            })
          };
        });

        // Extraer períodos
        this.allPeriodos = this.extractPeriodos(processedFiles);

        // Aplicar filtros y actualizar vistas
        this.applyFilters();

        this.changeDetectorRefs.detectChanges();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error al cargar archivos:', error);
        this.isLoading = false;
      }
    });
  }

  // === APLICAR TODOS LOS FILTROS ===
  applyFilters(): void {
    let filteredFiles = [...this.allFiles]; // Empezar con todos los archivos

    // Filtrar por período
    if (this.selectedPeriodo && this.selectedPeriodo.trim() !== '') {
      filteredFiles = filteredFiles.filter((file: any) => {
        if (!file.fileNumber || file.fileNumber.length < 6) return false;
        const filePeriodo = file.fileNumber.substring(0, 6);
        return filePeriodo === this.selectedPeriodo;
      });
    }

    // Filtrar por búsqueda de texto
    if (this.searchValue && this.searchValue.trim() !== '') {
      const searchTerm = this.searchValue.toLowerCase();
      filteredFiles = filteredFiles.filter((item: any) => {
        return (
          item.fileNumber?.toLowerCase().includes(searchTerm) ||
          item.fileDisplayState?.toLowerCase().includes(searchTerm)
        );
      });
    }

    // Actualizar vistas con datos filtrados
    this.updateViews(filteredFiles);
  }
  private updateViews(filteredFiles: File[]): void {
    // Actualizar tabla de archivos
    this.fileDataSource.data = filteredFiles;

    // Actualizar conteos
    this.updateFileCounts(filteredFiles);
  }

  // === CONTEO DE ARCHIVOS ===
  private updateFileCounts(files: File[]): void {
    const countObject: any = {};

    // Inicializar contadores en 0
    this.fileDisplayedColumns2.forEach((column) => {
      if (column !== 'TOTAL') {
        countObject[column] = 0;
      }
    });

    files.forEach((file) => {
      // Incrementar contador
      if (
        file.fileDisplayState &&
        this.fileDisplayedColumns2.includes(file.fileDisplayState)
      ) {
        countObject[file.fileDisplayState]++;
      }
    });

    // Calcular total
    const total = Object.values(countObject).reduce(
      (sum: number, count: any) => sum + count,
      0
    );
    this.totalTotal = total;
    countObject['TOTAL'] = total;

    // Actualizar dataSource
    this.fileCountsDataSource.data = [countObject];
  }

  // === EXTRACCIÓN DE PERÍODOS ===
  private extractPeriodos(files: File[]): Periodo[] {
    const periodosSet = new Set<string>();

    files.forEach((file) => {
      if (file.fileNumber && file.fileNumber.length >= 6) {
        const periodoCodigo = file.fileNumber.substring(0, 6);
        periodosSet.add(periodoCodigo);
      }
    });

    return Array.from(periodosSet)
      .map((codigo) => ({
        codigo: codigo,
        descripcion: this.formatPeriodoDescripcion(codigo)
      }))
      .sort((a, b) => b.codigo.localeCompare(a.codigo));
  }

  // === MÉTODOS DE UTILIDAD ===
  formatPeriodoDescripcion(codigo: string): string {
    if (codigo.length !== 6) return codigo;
    const año = codigo.substring(0, 4);
    const mes = codigo.substring(4, 6);
    return `${año}-${mes}`;
  }

  private _filterPeriodos(value: string): Periodo[] {
    const filterValue = value.toLowerCase();
    return this.allPeriodos.filter(
      (periodo) =>
        periodo.codigo.toLowerCase().includes(filterValue) ||
        periodo.descripcion.toLowerCase().includes(filterValue)
    );
  }

  getOptionText(option: Periodo): string {
    return option ? option.descripcion : '';
  }

  // === EVENTOS DE INTERFAZ ===
  onPeriodoSelected(periodo: Periodo): void {
    this.selectedPeriodo = periodo.codigo;
    this.applyFilters();
  }

  clearFilters(): void {
    this.selectedPeriodo = '';
    this.searchValue = '';
    this.fileControl.setValue('');
    this.applyFilters();
  }

  // === GETTERS PARA EL TEMPLATE ===
  get totalArchivos(): number {
    return this.fileDataSource.data.length;
  }

  get totalPeriodos(): number {
    return this.allPeriodos.length;
  }
}
